import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SubPageHero from "@/components/landing/SubPageHero";
import InvestorAccessModal from "@/components/landing/InvestorAccessModal";
import PartnerCard from "@/components/landing/PartnerCard";
import { LANDING } from "@/constants/testIds";

/**
 * Senior partners — two tiers, each with full About Me sections and
 * portrait placeholders.
 *  · FOUNDING_PARTNERS: Marcel Kellermann, Chris Reid
 *  · PARTNERS: Rachel Yang (Asia-Pacific), plus capacity for additional
 *    regional and functional partners as the platform grows.
 */
const FOUNDING_PARTNERS = [
  {
    id: "mk",
    anchor: "marcel-kellermann",
    initials: "MK",
    name: "Marcel Kellermann",
    role: "Managing Partner",
    subtitle:
      "Investor Relations · Capital Formation · Investment Strategy",
    photo: "/portraits/marcel-kellermann.jpg",
    bio: [
      "Marcel Kellermann is Co-Founder and Managing Partner of Kellermann Reid & Co., where he leads investor relations, capital formation, investment strategy, and institutional partnership development.",
      "Marcel brings more than a decade of experience across investment execution, strategic advisory, business transformation, capital raising, and private market transactions. His background combines investment management, market analysis, commercial structuring, and operational leadership across international mandates.",
      "At Kellermann Reid & Co., Marcel focuses on investor engagement, capital formation strategy, fund positioning, and the development of long-term institutional relationships.",
    ],
    pillars: [
      { label: "Focus", value: "Investor Relations" },
      { label: "Mandate", value: "Capital Formation" },
      { label: "Tenure", value: "Multi-Cycle" },
    ],
    email: "marcel.kellermann@krprivatecapital.com",
    linkedin: "https://www.linkedin.com/in/marcel-kellermann/",
  },
  {
    id: "cr",
    anchor: "chris-reid",
    initials: "CR",
    name: "Chris Reid",
    role: "Managing Partner",
    subtitle:
      "Commercial Finance · Strategy · Capital Partnerships",
    photo: "/portraits/chris-reid.jpg",
    bio: [
      "Chris Reid is Co-Founder and Managing Partner of Kellermann Reid & Co., where he supports the firm's commercial strategy, capital partnership development, operational execution, and fund platform coordination.",
      "Chris has a background in commercial finance brokerage, consulting, and financial services, with experience supporting funding pathways, client relationships, and strategic transaction discussions. His commercial finance experience strengthens the firm's ability to engage with lenders, investors, service providers, and strategic partners.",
      "At Kellermann Reid & Co., Chris focuses on platform development, capital partnerships, operational readiness, and the commercial execution required to build an institutional-grade private capital business.",
    ],
    pillars: [
      { label: "Focus", value: "Capital Partnerships" },
      { label: "Mandate", value: "Platform Build" },
      { label: "Approach", value: "Commercial" },
    ],
    email: "chris.reid@krprivatecapital.com",
    linkedin: "https://www.linkedin.com/in/chris-reid-847a0133/",
  },
];

const PARTNERS = [
  {
    id: "ry",
    anchor: "rachel-yang",
    initials: "RY",
    name: "Rachel Yang",
    role: "Partner · Asia-Pacific",
    subtitle:
      "Deal Origination · Investor Relations · Asia-Pacific Coverage",
    photo: "/portraits/rachel-yang.jpg",
    bio: [
      "Rachel Yang is Partner for Asia-Pacific at Kellermann Reid & Co. and the founder of a premier financial consultancy. She leads the platform's Asia-Pacific origination and investor coverage, with particular focus on private and institutional capital relationships across the region.",
      "Rachel brings years of rigorous experience from major banking institutions, with deep expertise across commercial finance and high-value asset finance. Her career has been built at the intersection of disciplined credit underwriting and bespoke client structuring — engaging counterparties on a documented, considered basis rather than a transactional one.",
      "Her core strengths sit in deal origination and investor relations: sourcing high-quality, mandate-aligned opportunities while cultivating sophisticated relationships with private and institutional investors across Asia-Pacific. She is recognised by counterparties for delivering elite financial solutions with absolute precision and market insight.",
      "At Kellermann Reid & Co., Rachel anchors the partnership's engagement with Asia-Pacific limited partners, single- and multi-family offices, and strategic capital partners — ensuring the platform's institutional posture is consistently expressed across the region.",
    ],
    pillars: [
      { label: "Focus", value: "Deal Origination" },
      { label: "Coverage", value: "Asia-Pacific" },
      { label: "Mandate", value: "Investor Relations" },
    ],
    email: "rachel.yang@krprivatecapital.com",
    linkedin: "https://www.linkedin.com/in/rachel-yang-44a91297/",
  },
  {
    id: "rl",
    anchor: "ryan-landry",
    initials: "RL",
    name: "Ryan Landry",
    role: "Partner · Strategic Capital",
    subtitle:
      "Strategic Capital · Family Offices · Investor Partnerships",
    photo: "/portraits/ryan-landry.jpg",
    bio: [
      "Ryan Landry is Partner for Strategic Capital at Kellermann Reid & Co. He leads the platform's engagement with family offices, single- and multi-family office capital partners, and global investment partner relationships — anchoring the strategic capital channel of the partnership.",
      "Ryan is an award-winning business operations executive with a career spanning multi-site startups, established businesses, private equity and venture capital enterprises. His experience covers capital fundraising, mergers and acquisitions, strategic acceleration, revenue growth and the orchestration of senior leadership teams through transformational growth phases.",
      "He brings particular depth in business transformations, process innovation, performance improvement and stakeholder management — with a documented track record of delivering executive-level outcomes through capital investments, franchise operations, brand creation and acquisitions. Ryan is a subject-matter expert in C-level communication, key relationship management and the cultivation of disciplined corporate culture.",
      "At Kellermann Reid & Co., Ryan cultivates long-term, by-introduction relationships with sophisticated global investors and institutional counterparties — ensuring the platform's strategic capital posture is consistently expressed across its global investor base.",
    ],
    pillars: [
      { label: "Focus", value: "Strategic Capital" },
      { label: "Coverage", value: "Family Offices" },
      { label: "Mandate", value: "Capital Formation" },
    ],
    email: "ryan.landry@krprivatecapital.com",
    linkedin: "https://www.linkedin.com/in/ryanmlandry/",
  },
];

/**
 * Reserved seats — anonymised, awaiting appointment / public disclosure.
 */
const RESERVED_ROLES = [
  {
    seal: "III",
    role: "Managing Director · Credit & Structuring",
    profile:
      "Senior credit principal with extensive experience across senior-secured, unitranche and bridge financings. Co-chair of the credit committee.",
    bias: "Senior-Secured · Covenant-Led · Defined Exits",
  },
  {
    seal: "IV",
    role: "Managing Director · Real Estate Credit",
    profile:
      "Specialist in industrial and operationally critical real estate financing across European and select North American markets.",
    bias: "Asset Coverage · Cash-Flow Durability · Documented Recourse",
  },
  {
    seal: "V",
    role: "Head of Risk, Legal & Compliance",
    profile:
      "Senior risk professional with regulatory, market and operational risk experience. Reporting line independent of the deal teams.",
    bias: "Conflicts Management · AML/KYC · Documented Diligence",
  },
  {
    seal: "VI",
    role: "Head of Limited Partner Relations",
    profile:
      "Senior IR background across institutional, family office and placement agent channels. Conducts confidential dialogue on documented terms.",
    bias: "Discretion · By Introduction · Documented Engagement",
  },
];

const PartnershipPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar onRequestAccess={openModal} />
      <main
        data-testid={LANDING.partnershipPage}
        className="relative bg-[var(--kr-bg)]"
      >
        <SubPageHero
          index="The Partnership"
          label="Co-Founders · Investment Committee · Operating Network"
          title={
            <>
              A senior partnership organised around{" "}
              <span className="italic text-[var(--kr-gold)]">documented</span>{" "}
              process.
            </>
          }
          description="Co-founded by Marcel Kellermann and Chris Reid, with Rachel Yang as Partner for Asia-Pacific and Ryan Landry as Partner for Strategic Capital, the platform is operated by a senior partnership supported by an Investment Committee, an Operating Partner Network and independent Risk, Legal & Compliance oversight. Investor relations sits outside the deal teams."
          breadcrumb="Partnership"
        />

        {/* Named partnership — two tiers */}
        <section
          data-testid={LANDING.partnershipPageFounders}
          className="py-20 lg:py-28"
        >
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
            {/* Tier 1 — Founding Partners */}
            <div className="flex items-center gap-4 mb-8">
              <span
                aria-hidden
                className="block w-[8px] h-[8px]"
                style={{
                  background: "var(--kr-gold)",
                  transform: "rotate(45deg)",
                }}
              />
              <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                Founding Partners
              </span>
              <span className="flex-1 h-px bg-[var(--kr-line)]" />
            </div>
            <div className="space-y-0 border-t border-[var(--kr-line-soft)] mb-20">
              {FOUNDING_PARTNERS.map((p, i) => (
                <PartnerCard key={p.id} partner={p} index={i} />
              ))}
            </div>

            {/* Tier 2 — Partners */}
            <div className="flex items-center gap-4 mb-8">
              <span
                aria-hidden
                className="block w-[8px] h-[8px]"
                style={{
                  background: "var(--kr-gold)",
                  transform: "rotate(45deg)",
                }}
              />
              <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                Partners
              </span>
              <span className="flex-1 h-px bg-[var(--kr-line)]" />
            </div>
            <div className="space-y-0 border-t border-[var(--kr-line-soft)]">
              {PARTNERS.map((p, i) => (
                <PartnerCard
                  key={p.id}
                  partner={p}
                  index={FOUNDING_PARTNERS.length + i}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Reserved seats */}
        <section
          data-testid={LANDING.partnershipPageReserved}
          className="py-20 lg:py-28 border-t border-[var(--kr-line-soft)]"
          style={{ background: "var(--kr-bg-2)" }}
        >
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-12 gap-12 mb-14">
              <div className="lg:col-span-7">
                <div className="flex items-center gap-4 mb-6">
                  <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                    Reserved Seats
                  </span>
                  <span className="w-12 h-px bg-[var(--kr-gold)]/60" />
                </div>
                <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.8rem,3.2vw,2.6rem)] leading-[1.15] max-w-[28ch]">
                  Senior seats are being filled with{" "}
                  <span className="italic text-[var(--kr-gold)]">
                    documented
                  </span>{" "}
                  discipline.
                </h2>
              </div>
              <div className="lg:col-span-5">
                <p className="text-[var(--kr-text-muted)] text-[15.5px] leading-[1.85] lg:pt-10">
                  Additional senior appointments are advanced on a confidential
                  basis. The roles below describe the partnership&apos;s composition
                  as it completes formation. Named biographies will be
                  published as appointments are finalised.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-0 border-t border-l border-[var(--kr-line)]">
              {RESERVED_ROLES.map((r) => (
                <article
                  key={r.seal}
                  data-testid={`${LANDING.partnershipPageRolePrefix}${r.seal}`}
                  className="relative p-10 border-r border-b border-[var(--kr-line)] group hover:bg-[rgba(201,169,110,0.03)] transition-colors duration-500"
                >
                  <div className="flex items-center justify-between mb-8">
                    <div
                      className="w-14 h-14 border border-[var(--kr-gold)]/60 flex items-center justify-center font-serif-display text-[1.4rem] italic text-[var(--kr-gold)]"
                      aria-hidden
                    >
                      {r.seal}
                    </div>
                    <span className="font-mono-label text-[9px] text-[var(--kr-text-dim)]">
                      Reserved
                    </span>
                  </div>
                  <h3 className="font-serif-display text-[1.55rem] text-[var(--kr-text)] mb-5">
                    {r.role}
                  </h3>
                  <p className="text-[var(--kr-text-muted)] text-[14.5px] leading-[1.75] mb-6">
                    {r.profile}
                  </p>
                  <div className="kr-rule mb-4" />
                  <div className="font-mono-label text-[9px] text-[var(--kr-text-muted)] mb-2">
                    Bias
                  </div>
                  <div className="text-[12.5px] text-[var(--kr-text)] leading-[1.6]">
                    {r.bias}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Committee strip */}
        <section className="border-t border-[var(--kr-line)] py-24 bg-[var(--kr-bg)]">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-4 mb-6">
                <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                  Governance
                </span>
                <span className="w-10 h-px bg-[var(--kr-gold)]/60" />
              </div>
              <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.8rem,3.2vw,2.6rem)] leading-[1.15] max-w-[26ch]">
                The Investment Committee is the{" "}
                <span className="italic text-[var(--kr-gold)]">
                  single forum
                </span>{" "}
                of decision.
              </h2>
            </div>
            <div className="lg:col-span-7 lg:pl-8">
              <p className="text-[var(--kr-text-muted)] text-[16px] leading-[1.85] mb-8 max-w-[60ch]">
                Every transaction is sponsored, debated and decided by
                committee — with documented diligence, written deliberation
                and recorded dissent. Independent perspectives are sought.
                Decisions are made on the basis of the platform&apos;s standard,
                not the urgency of the moment.
              </p>
              <Link
                to="/#governance"
                className="kr-link font-mono-label text-[11px]"
              >
                Read the Governance Framework →
              </Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="border-t border-[var(--kr-line)] py-20">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 flex flex-wrap gap-6 items-center justify-between">
            <h3 className="font-serif-display text-[var(--kr-text)] text-[1.7rem] leading-[1.2] max-w-[40ch]">
              Initiate a{" "}
              <span className="italic text-[var(--kr-gold)]">confidential</span>{" "}
              dialogue with the partnership.
            </h3>
            <div className="flex flex-wrap gap-4">
              <button
                type="button"
                onClick={openModal}
                data-testid={LANDING.partnershipPageRequest}
                className="kr-btn-primary"
              >
                Request Investor Access
                <span aria-hidden>→</span>
              </button>
              <Link to="/#contact" className="kr-btn-ghost">
                General Enquiry
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <InvestorAccessModal open={modalOpen} onClose={closeModal} />
    </>
  );
};

export default PartnershipPage;
